import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { TIMEOUTS } from '../config/constants'

export type NotificationType = 'error' | 'warning' | 'info'

export interface NotificationAction {
  label: string
  onClick: () => void
}

export type NotifyFn = (message: string, type: NotificationType, persistent?: boolean, action?: NotificationAction) => void

interface Notification {
  id: number
  message: string
  type: NotificationType
  persistent: boolean
  action?: NotificationAction
}

const NotificationContext = createContext<NotifyFn>(() => {})

// eslint-disable-next-line react-refresh/only-export-components
export function useNotify(): NotifyFn {
  return useContext(NotificationContext)
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const nextId = useRef(0)

  const notify: NotifyFn = useCallback((message, type, persistent = false, action?) => {
    const id = nextId.current++
    setNotifications(prev => [...prev, { id, message, type, persistent, action }])
  }, [])

  const dismiss = useCallback((id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }, [])

  return (
    <NotificationContext.Provider value={notify}>
      {children}
      <NotificationBar notifications={notifications} onDismiss={dismiss} />
    </NotificationContext.Provider>
  )
}

const typeStyles = {
  error: 'border-red-600 bg-red-50 text-red-800 dark:bg-red-950/80 dark:text-red-200',
  warning: 'border-amber-500 bg-amber-50 text-amber-800 dark:bg-amber-950/80 dark:text-amber-200',
  info: 'border-blue-500 bg-blue-50 text-blue-800 dark:bg-blue-950/80 dark:text-blue-200',
}

function NotificationBar({ notifications, onDismiss }: {
  notifications: Notification[]
  onDismiss: (id: number) => void
}) {
  if (notifications.length === 0) return null

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {notifications.map(n => (
        <NotificationItem key={n.id} notification={n} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

function NotificationItem({ notification, onDismiss }: {
  notification: Notification
  onDismiss: (id: number) => void
}) {
  useEffect(() => {
    if (notification.persistent) return
    const timeout = notification.type === 'error' ? TIMEOUTS.NOTIFICATION_ERROR_MS : TIMEOUTS.NOTIFICATION_INFO_MS
    const timer = setTimeout(() => onDismiss(notification.id), timeout)
    return () => clearTimeout(timer)
  }, [notification.id, notification.type, notification.persistent, onDismiss])

  return (
    <div className={`border-l-4 px-4 py-3 text-sm font-medium shadow-lg ${typeStyles[notification.type]} flex items-start gap-2`}>
      <span className="flex-1">{notification.message}</span>
      {notification.action && (
        <button
          onClick={() => { notification.action!.onClick(); onDismiss(notification.id) }}
          className="underline hover:no-underline transition-opacity text-current font-semibold whitespace-nowrap"
        >
          {notification.action.label}
        </button>
      )}
      <button
        onClick={() => onDismiss(notification.id)}
        className="opacity-60 hover:opacity-100 transition-opacity text-current"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  )
}
